import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ThemeProvider } from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SupervisedUserCircleRoundedIcon from '@mui/icons-material/SupervisedUserCircleRounded';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { temaEdicao } from '../../components/theme/Theme';
import { UserInfo } from '../../dto/UserInfo';
import { IndicadoresGraficosService } from '../../services/IndicadoresGraficosService';
import PedidosPeriodoChartCard from './components/PedidosPeriodoChartCard';
import StatusPedidosBarChartCard from './components/StatusPedidosBarChartCard';
import IndicadoresHeaderFiltros from './components/IndicadoresHeaderFiltros';
import IndicadoresResumoKpis from './components/IndicadoresResumoKpis';
import RankingChartCard from './components/RankingChartCard';
import {
  IndicadorChart,
  IndicadorItensMaisVendidosChart,
  IndicadorStatusPedidosChart,
  IndicadorTopClientesChart,
  IndicadorTopRepresentantesChart,
} from './IndicadoresGraficos.types';
import {
  formatarMoeda,
  PeriodoFiltro,
  carregarIndicadores,
  carregarItensMaisVendidos,
  carregarStatusPedidos,
  carregarTopClientes,
  carregarTopRepresentantes,
} from './IndicadoresGraficos.loaders';
import './IndicadoresGraficos.css';
import { DadoRanking } from '../../components/customRankingChart/CustomRankingChart';

const LIMITE_MAXIMO_RANKING_ITENS = 15;

const EMPTY_ICON_CLIENTES = <GroupsRoundedIcon className='indicadores-empty-icon' />;
const EMPTY_ICON_ITENS = <QueryStatsRoundedIcon className='indicadores-empty-icon' />;
const EMPTY_ICON_REPRESENTANTES = <SupervisedUserCircleRoundedIcon className='indicadores-empty-icon' />;

type AgenteFiltroOption = {
  id: string;
  nome: string;
};

function IndicadoresGraficos() {
  const navigate = useNavigate();
  const userInfo: UserInfo = JSON.parse(localStorage.getItem('udbs')!);
  const [indicadoresService] = useState(
    () => new IndicadoresGraficosService(() => navigate('/mvp', { state: { propsPage: 'login' } }))
  );
  const idEmpresa = userInfo.idEmpresa;
  const idAgente = userInfo.idAgente;
  const isAdm = Boolean(userInfo.isAdm);

  const [dados, setDados] = useState<IndicadorChart[]>([]);
  const [dadosItensMaisVendidos, setDadosItensMaisVendidos] = useState<IndicadorItensMaisVendidosChart[]>([]);
  const [dadosStatusPedidos, setDadosStatusPedidos] = useState<IndicadorStatusPedidosChart[]>([]);
  const [dadosTopClientes, setDadosTopClientes] = useState<IndicadorTopClientesChart[]>([]);
  const [dadosTopRepresentantes, setDadosTopRepresentantes] = useState<IndicadorTopRepresentantesChart[]>([]);
  const [isLoadingPedidos, setIsLoadingPedidos] = useState(false);
  const [isLoadingItens, setIsLoadingItens] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isLoadingClientes, setIsLoadingClientes] = useState(false);
  const [isLoadingRepresentantes, setIsLoadingRepresentantes] = useState(false);
  const [limiteRankingItens, setLimiteRankingItens] = useState(10);
  const [limiteRankingClientes, setLimiteRankingClientes] = useState(10);
  const [limiteRankingRepresentantes, setLimiteRankingRepresentantes] = useState(10);
  const [agentesFiltro, setAgentesFiltro] = useState<AgenteFiltroOption[]>([]);
  const [idAgenteFiltro, setIdAgenteFiltro] = useState('');
  const [periodoFiltro, setPeriodoFiltro] = useState<PeriodoFiltro>('ultima_semana');
  const [periodoAtivo, setPeriodoAtivo] = useState<PeriodoFiltro>('ultima_semana');
  const filtrarPorAgenteRepresentante = Boolean(idAgenteFiltro);

  const carregarDashboard = useCallback((periodo: PeriodoFiltro) => {
    const agenteSelecionado = idAgenteFiltro || undefined;

    setPeriodoAtivo(periodo);
    void carregarIndicadores({
      periodo,
      idEmpresa,
      idAgenteLogado: idAgente,
      idAgenteFiltro: agenteSelecionado,
      filtrarPorAgenteRepresentante,
      indicadoresService,
      setDados,
      setIsLoading: setIsLoadingPedidos,
    });
    void carregarItensMaisVendidos({
      periodo,
      idEmpresa,
      idAgenteLogado: idAgente,
      idAgenteFiltro: agenteSelecionado,
      filtrarPorAgenteRepresentante,
      limiteRanking: LIMITE_MAXIMO_RANKING_ITENS,
      indicadoresService,
      setDados: setDadosItensMaisVendidos,
      setIsLoading: setIsLoadingItens,
    });
    void carregarStatusPedidos({
      periodo,
      idEmpresa,
      idAgenteLogado: idAgente,
      idAgenteFiltro: agenteSelecionado,
      filtrarPorAgenteRepresentante,
      indicadoresService,
      setDados: setDadosStatusPedidos,
      setIsLoading: setIsLoadingStatus,
    });
    void carregarTopClientes({
      periodo,
      idEmpresa,
      idAgenteLogado: idAgente,
      idAgenteFiltro: agenteSelecionado,
      filtrarPorAgenteRepresentante,
      indicadoresService,
      setDados: setDadosTopClientes,
      setIsLoading: setIsLoadingClientes,
    });

    if (isAdm) {
      void carregarTopRepresentantes({
        periodo,
        idEmpresa,
        idAgenteLogado: idAgente,
        idAgenteFiltro: agenteSelecionado,
        filtrarPorAgenteRepresentante,
        indicadoresService,
        setDados: setDadosTopRepresentantes,
        setIsLoading: setIsLoadingRepresentantes,
      });
    } else {
      setDadosTopRepresentantes([]);
      setIsLoadingRepresentantes(false);
    }
  }, [filtrarPorAgenteRepresentante, idAgente, idAgenteFiltro, idEmpresa, indicadoresService, isAdm]);

  useEffect(() => {
    if (!isAdm) {
      setAgentesFiltro([]);
      setIdAgenteFiltro('');
      return;
    }

    let ativo = true;

    const carregarAgentesFiltro = async () => {
      const response = await indicadoresService.get(
        `/v1/indicadores-graficos/representantes-empresa?idEmpresa=${idEmpresa}&periodo=${periodoFiltro}`
      );

      if (!ativo) {
        return;
      }

      if (response.codigo !== 200) {
        setAgentesFiltro([]);
        return;
      }

      const statusBruto = response.status as any;
      const listaAgentes = Array.isArray(statusBruto)
        ? statusBruto
        : statusBruto?.agentes || statusBruto?.lista || [];

      const agentesFormatados = (listaAgentes as any[])
        .map((item) => {
          const id = String(item.idAgente || item.id_agente_representante || '').trim();
          const nome = String(item.nome || item.nomeFantasia || item.nome_representante || '').trim();

          return {
            id,
            nome: nome || id,
          };
        })
        .filter((item) => item.id !== '');

      setAgentesFiltro(agentesFormatados);
    };

    void carregarAgentesFiltro();

    return () => {
      ativo = false;
    };
  }, [idAgente, idEmpresa, indicadoresService, isAdm, periodoFiltro]);

  useEffect(() => {
    carregarDashboard(periodoFiltro);
  }, [carregarDashboard, periodoFiltro]);

  const totalPedidos = dados.reduce((acc, item) => acc + item.qtdPedidos, 0);
  const totalFaturado = dados.reduce((acc, item) => acc + item.totalFaturado, 0);
  const ticketMedio = totalPedidos > 0 ? totalFaturado / totalPedidos : 0;
  const dadosItensMaisVendidosFiltrados = useMemo(
    () => dadosItensMaisVendidos.slice(0, limiteRankingItens),
    [dadosItensMaisVendidos, limiteRankingItens]
  );
  const dadosTopClientesFiltrados = useMemo(
    () => dadosTopClientes.slice(0, limiteRankingClientes),
    [dadosTopClientes, limiteRankingClientes]
  );
  const dadosTopClientesRanking = useMemo<DadoRanking[]>(
    () => dadosTopClientesFiltrados.map((item) => ({
      codigoItem: '',
      nomeItem: item.nomeCliente,
      quantidadeVendida: item.qtdPedidos,
      rotuloRanking: item.rotuloRanking,
      valorTotal: item.totalGasto,
    })),
    [dadosTopClientesFiltrados]
  );
  const dadosTopRepresentantesFiltrados = useMemo(
    () => dadosTopRepresentantes.slice(0, limiteRankingRepresentantes),
    [dadosTopRepresentantes, limiteRankingRepresentantes]
  );
  const dadosTopRepresentantesRanking = useMemo<DadoRanking[]>(
    () => dadosTopRepresentantesFiltrados.map((item) => ({
      codigoItem: '',
      nomeItem: item.nomeRepresentante,
      quantidadeVendida: item.qtdPedidos,
      rotuloRanking: item.rotuloRanking,
      valorTotal: item.totalGasto,
    })),
    [dadosTopRepresentantesFiltrados]
  );

  const handleLimiteItensChange = useCallback((novoLimite: number) => setLimiteRankingItens(novoLimite), []);
  const handleLimiteClientesChange = useCallback((novoLimite: number) => setLimiteRankingClientes(novoLimite), []);
  const handleLimiteRepresentantesChange = useCallback(
    (novoLimite: number) => setLimiteRankingRepresentantes(novoLimite),
    []
  );

  return (
    <ThemeProvider theme={temaEdicao}>
      <Box className='indicadores-page'>

        <IndicadoresHeaderFiltros
          periodoFiltro={periodoFiltro}
          onPeriodoChange={setPeriodoFiltro}
          mostrarFiltroAgente={isAdm}
          agenteFiltro={idAgenteFiltro}
          agentesOptions={agentesFiltro}
          onAgenteChange={setIdAgenteFiltro}
          kpiResumo={(
            <IndicadoresResumoKpis
              dadosStatusPedidos={dadosStatusPedidos}
              totalFaturado={totalFaturado}
              ticketMedio={ticketMedio}
              formatarMoeda={formatarMoeda}
            />
          )}
        />

        {/* ── Gráficos ── */}
        <Box className='indicadores-charts-grid'>

          <StatusPedidosBarChartCard
            isLoading={isLoadingStatus}
            dados={dadosStatusPedidos}
          />

           {isAdm && (
            <>
              <PedidosPeriodoChartCard
                isLoading={isLoadingPedidos}
                dados={dados}
                periodo={periodoAtivo}
              />

             
            </>
          )}

          <RankingChartCard
              isLoading={isLoadingItens}
              dados={dadosItensMaisVendidosFiltrados}
              titulo='Ranking de Itens'
              subtitulo='Ranking dos itens mais vendidos no período selecionado.'
              limiteRanking={limiteRankingItens}
              onLimiteRankingChange={handleLimiteItensChange}
              emptyIcon={EMPTY_ICON_ITENS}
              emptyTitle='Nenhum item encontrado para o período selecionado.'
              emptyDescription='Ajuste o intervalo de datas e clique em Atualizar.'
            />

          <RankingChartCard
            isLoading={isLoadingClientes}
            dados={dadosTopClientesRanking}
            titulo='Ranking de Clientes'
            subtitulo='Ranking por quantidade de pedidos no período selecionado.'
            tituloQuantidade='Qtd. Pedidos'
            rotuloValorExtra='Total Gasto'
            formatarValorExtra={formatarMoeda}
            limiteRanking={limiteRankingClientes}
            onLimiteRankingChange={handleLimiteClientesChange}
            emptyIcon={EMPTY_ICON_CLIENTES}
            emptyTitle='Nenhum cliente encontrado para o período selecionado.'
            emptyDescription='Ajuste o filtro e clique em Atualizar para carregar o ranking.'
          />          

          {isAdm && (
            <>
              <RankingChartCard
                isLoading={isLoadingRepresentantes}
                dados={dadosTopRepresentantesRanking}
                titulo='Ranking de Representantes'
                subtitulo='Ranking por quantidade de pedidos no período selecionado.'
                tituloQuantidade='Qtd. Pedidos'
                rotuloValorExtra='Total Vendido'
                formatarValorExtra={formatarMoeda}
                limiteRanking={limiteRankingRepresentantes}
                onLimiteRankingChange={handleLimiteRepresentantesChange}
                emptyIcon={EMPTY_ICON_REPRESENTANTES}
                emptyTitle='Nenhum representante encontrado para o período selecionado.'
                emptyDescription='Ajuste os filtros e clique em Atualizar para carregar o ranking.'
              />
            </>
          )}

        </Box>{/* fim indicadores-charts-grid */}

        <ToastContainer />
      </Box>
    </ThemeProvider>
  );
}

export default IndicadoresGraficos;
